import { useState, useEffect, useRef } from 'react';
import {
    Box, Heading, SimpleGrid, Text,
    Button, Badge, HStack, useToast, Icon, Flex, Select, Center, Card, CardBody
} from '@chakra-ui/react';
import { FiAlertCircle, FiArrowLeft, FiRefreshCw, FiBookOpen } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import StudentCard, { STATUS_CONFIG, pulse } from '../components/StudentCard';
import { filterClassesForStaffUser } from '../utils/teacherClassAccess';

const MyClassPage = () => {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);

    // Smart Selection States
    const [allClasses, setAllClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState('');
    const [isClassLoading, setIsClassLoading] = useState(true);

    const toast = useToast();
    const navigate = useNavigate();

    // Polling Ref
    const intervalRef = useRef(null);

    // 1. Fetch Classes for Teacher
    useEffect(() => {
        const fetchClasses = async () => {
            try {
                // Fetch Lite classes (fast)
                const res = await authAPI.getClassesLite();
                let classesData = res.data;

                // Filter for current teacher
                classesData = filterClassesForStaffUser(classesData, user);

                setAllClasses(classesData);

                // Smart Selection Logic
                if (classesData.length === 1) {
                    // Single Class -> Auto Select
                    setSelectedClassId(classesData[0].id.toString());
                } else {
                    // Multi Class -> No Auto Select (Wait for user)
                    // If previously selected, keep it? Maybe, but simple is robust.
                }

            } catch (error) {
                console.error("Failed to load classes", error);
                toast({ title: "無法載入班級列表", status: "error" });
            } finally {
                setIsClassLoading(false);
            }
        };
        fetchClasses();
    }, [user]); // Re-run if user changes (e.g. login)

    // 2. Fetch Students (Dependent on selectedClassId)
    const fetchData = async (isBackground = false) => {
        if (!selectedClassId) return;

        try {
            if (!isBackground) setLoading(true);

            // Pass class_id filter
            const res = await authAPI.getStudents({
                limit: 1000,
                class_id: selectedClassId
            });

            // Sort Students by Priority
            const sorted = res.data.sort((a, b) => {
                const pA = STATUS_CONFIG[a.status]?.priority ?? 99;
                const pB = STATUS_CONFIG[b.status]?.priority ?? 99;
                return pA - pB;
            });

            setStudents(sorted);
        } catch (error) {
            console.error(error);
            if (!isBackground) {
                toast({ title: "無法獲取班級資料", status: "error" });
            }
        } finally {
            if (!isBackground) setLoading(false);
        }
    };

    // 3. Effect to Trigger Fetch on Class Change
    useEffect(() => {
        if (selectedClassId) {
            fetchData();
            // Setup Polling
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => fetchData(true), 10000);
        } else {
            setStudents([]);
            if (intervalRef.current) clearInterval(intervalRef.current);
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [selectedClassId]);

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            // Optimistic Update
            setStudents(prev => prev.map(s =>
                s.id === id ? { ...s, status: newStatus } : s
            ));

            await authAPI.updateStudentStatus(id, newStatus);
            // Re-fetch to sync
            fetchData(true);
        } catch (error) {
            toast({ title: "更新失敗", status: "error" });
            fetchData(true); // Revert
        }
    };

    // Helper: Find current class name
    const currentClassName = allClasses.find(c => c.id.toString() === selectedClassId)?.name;

    return (
        <Flex direction="column" h="100%" w="100%">
            {/* Header - Fixed */}
            <Box flexShrink={0} mb={6}>
                <HStack justifyContent="space-between" align="center" wrap="wrap" gap={4}>
                    <HStack spacing={4}>


                        {/* Dynamic Header: Text or Dropdown */}
                        {allClasses.length > 1 ? (
                            <HStack>
                                <Heading size="lg" color="brand.800" whiteSpace="nowrap">我的班級</Heading>
                                <Select
                                    placeholder="請選擇班級..."
                                    value={selectedClassId}
                                    onChange={(e) => setSelectedClassId(e.target.value)}
                                    bg="white"
                                    width="200px"
                                    borderColor="brand.300"
                                    fontWeight="bold"
                                >
                                    {allClasses.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </Select>
                            </HStack>
                        ) : (
                            <HStack>
                                <Heading size="lg" color="brand.800">
                                    {currentClassName || "我的班級"}
                                </Heading>
                            </HStack>
                        )}

                        {selectedClassId && (
                            <Badge colorScheme="blue" fontSize="md" p={1} borderRadius="md">
                                {students.length} 位學生
                            </Badge>
                        )}
                    </HStack>

                    {selectedClassId && (
                        <Button leftIcon={<FiRefreshCw />} size="sm" onClick={() => fetchData()}>
                            重整
                        </Button>
                    )}
                </HStack>

                {/* Urgent Alert Banner (Only if class selected) */}
                {selectedClassId && students.some(s => s.status === 'PARENT_EN_ROUTE') && (
                    <Box bg="red.500" color="white" p={3} borderRadius="md" mt={4} display="flex" alignItems="center" animation={`${pulse} 2s infinite`}>
                        <Icon as={FiAlertCircle} boxSize={6} mr={3} />
                        <Text fontWeight="bold" fontSize="lg">
                            注意：有 {students.filter(s => s.status === 'PARENT_EN_ROUTE').length} 位家長正在路途中！請優先關注。
                        </Text>
                    </Box>
                )}
            </Box>

            {/* Content Grid - Scrollable */}
            <Box flex="1" overflowY="auto" overflowX="hidden" pb={4} px={1}>
                {!selectedClassId ? (
                    // Placeholder for No Selection
                    <Center h="50vh" flexDirection="column" color="gray.400">
                        <Icon as={FiBookOpen} boxSize={16} mb={4} color="gray.300" />
                        <Heading size="md" color="gray.500" mb={2}>
                            {isClassLoading ? "正在載入班級資料..." : "請先選擇要檢視的班級"}
                        </Heading>
                        {!isClassLoading && (
                            <Text>從上方選單選擇您的班級以開始管理</Text>
                        )}
                    </Center>
                ) : (
                    // Student Grid
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
                        {students.map(student => (
                            <StudentCard
                                key={student.id}
                                student={student}
                                onUpdate={handleStatusUpdate}
                            />
                        ))}
                    </SimpleGrid>
                )}
            </Box>
        </Flex>
    );
};

export default MyClassPage;
